import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import NewsCard from '../../components/news/NewsCard';
import ToolCard from '../../components/tools/ToolCard';
import ThreadCard from '../../components/threads/ThreadCard';
import { Link } from 'react-router-dom';
import Logo from '../../components/common/Logo';
import { generateNewsFromUrl, generateNewsFromText } from '../../lib/aiService';
import supabase from '../../supabase/supabase';
import { useTheme } from '../../context/ThemeContext';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('News');
  const [adminMode, setAdminMode] = useState('write'); // 'write' or 'manage'
  
  // Theme Context
  const { theme, updateGlobalTheme } = useTheme();

  // Editor State
  const [urlInput, setUrlInput] = useState('');
  const [manualTextInput, setManualTextInput] = useState('');
  const [useManualText, setUseManualText] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [viewMode, setViewMode] = useState('editor'); // mobile view toggle

  // Manage State
  const [items, setItems] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);

  const initialDraft = {
    id: null,
    category: "",
    hook: "",
    summary: "",
    points: ["", "", ""],
    whyItMatters: "",
    source: "",
    date: "Just now",
    readTime: "",
    deepLinks: [],
    ctaText: "",
    ctaUrl: "",
    isFeatured: false,
    logo: "", // Added for Tools
    usedFallback: false,
    status: "draft"
  };

  const [draft, setDraft] = useState(initialDraft);

  useEffect(() => {
    if (adminMode === 'manage' && activeTab !== 'Settings') {
      fetchItems();
    }
  }, [activeTab, adminMode]);

  const fetchItems = async () => {
    setIsLoadingItems(true);
    try {
      const table = activeTab === 'News' ? 'ai_news' : activeTab === 'Tools' ? 'ai_tools' : 'viral_threads';
      const { data, error } = await supabase.from(table).select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('Error fetching items:', err);
    } finally {
      setIsLoadingItems(false);
    }
  };

  const handleEditItem = (item) => {
    let mappedDraft = {
      ...initialDraft,
      id: item.id,
      status: item.status,
      category: item.category || "",
      isFeatured: item.is_featured || false
    };

    if (activeTab === 'News') {
      mappedDraft = {
        ...mappedDraft,
        hook: item.hook || item.title || "",
        summary: item.summary || "",
        points: item.points || ["", "", ""],
        whyItMatters: item.why_it_matters || "",
        source: item.source || "",
        readTime: item.read_time || "",
        deepLinks: item.deep_links || [],
        ctaText: item.cta_text || "",
        ctaUrl: item.cta_url || ""
      };
    } else if (activeTab === 'Tools') {
      mappedDraft = {
        ...mappedDraft,
        hook: item.name || item.hook || "",
        summary: item.description || "",
        deepLinks: item.deep_links || [],
        ctaText: item.cta_text || "",
        ctaUrl: item.cta_url || "",
        logo: item.logo || ""
      };
    } else if (activeTab === 'Viral Threads') {
      mappedDraft = {
        ...mappedDraft,
        hook: item.title || "",
        summary: item.content || "",
        deepLinks: item.deep_links || [],
        ctaText: item.cta_text || "",
        ctaUrl: item.cta_url || ""
      };
    }
    setDraft(mappedDraft);
    setAdminMode('write');
  };

  const handleViewItem = (item) => {
    handleEditItem(item);
    setViewMode('preview');
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      const table = activeTab === 'News' ? 'ai_news' : activeTab === 'Tools' ? 'ai_tools' : 'viral_threads';
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
      fetchItems(); // refresh list
    } catch (err) {
      console.error('Error deleting item:', err);
      alert("Failed to delete item.");
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setProgressMsg('Initializing...');
    setError(null);
    setSuccess(null);

    try {
      let generatedContent;
      if (useManualText) {
        if (!manualTextInput.trim()) throw new Error("Please paste the article text.");
        setProgressMsg('Creating Pulse5 summary...');
        const host = urlInput ? new URL(urlInput).hostname.replace('www.', '') : 'Manual Entry';
        generatedContent = await generateNewsFromText(manualTextInput, host);
        generatedContent.usedFallback = false;
      } else {
        if (!urlInput) throw new Error("Please enter a URL.");
        generatedContent = await generateNewsFromUrl(urlInput, setProgressMsg);
      }

      if (!useManualText && urlInput) {
        generatedContent.ctaUrl = urlInput;
      }
      
      setDraft(prev => ({ ...prev, ...generatedContent, id: null })); // new generation clears ID
    } catch (err) {
      console.error("Generation failed:", err);
      setError(err.message || 'Failed to generate content. Please try again.');
      if (err.message.includes('paste the article text manually')) {
        setUseManualText(true);
      }
    } finally {
      setIsGenerating(false);
      setProgressMsg('');
    }
  };

  const handleSave = async (status) => {
    setIsGenerating(true);
    setError(null);
    setSuccess(null);
    setProgressMsg(`Saving as ${status}...`);

    try {
      let table = 'ai_news';
      let payload = {};

      if (activeTab === 'News') {
        payload = {
          title: draft.hook || 'Untitled',
          hook: draft.hook,
          summary: draft.summary,
          category: draft.category,
          points: draft.points,
          why_it_matters: draft.whyItMatters,
          source: draft.source,
          read_time: draft.readTime,
          deep_links: draft.deepLinks,
          cta_text: draft.ctaText,
          cta_url: draft.ctaUrl,
          is_featured: draft.isFeatured,
          status
        };
      } else if (activeTab === 'Tools') {
        table = 'ai_tools';
        payload = {
          name: draft.hook || 'New Tool',
          hook: draft.hook,
          description: draft.summary,
          category: draft.category,
          deep_links: draft.deepLinks,
          cta_text: draft.ctaText,
          cta_url: draft.ctaUrl,
          is_featured: draft.isFeatured,
          logo: draft.logo,
          status
        };
      } else if (activeTab === 'Viral Threads') {
        table = 'viral_threads';
        payload = {
          title: draft.hook || 'New Thread',
          content: draft.summary,
          category: draft.category,
          deep_links: draft.deepLinks,
          cta_text: draft.ctaText,
          cta_url: draft.ctaUrl,
          is_featured: draft.isFeatured,
          status
        };
      }

      if (draft.id) {
        // Update existing
        const { error: dbError } = await supabase.from(table).update(payload).eq('id', draft.id);
        if (dbError) throw dbError;
      } else {
        // Insert new
        const { data, error: dbError } = await supabase.from(table).insert([payload]).select().single();
        if (dbError) throw dbError;
        if (data) setDraft(prev => ({ ...prev, id: data.id })); // set the new ID so subsequent saves are updates
      }

      setSuccess(`Successfully ${status === 'draft' ? 'saved to drafts' : 'published'}!`);
      setDraft(prev => ({ ...prev, status }));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
      setProgressMsg('');
    }
  };

  const handleFieldChange = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));
  
  const handlePointChange = (index, value) => {
    const newPoints = [...draft.points];
    newPoints[index] = value;
    setDraft(prev => ({ ...prev, points: newPoints }));
  };

  const handleAddDeepLink = () => {
    setDraft(prev => ({
      ...prev,
      deepLinks: [...(prev.deepLinks || []), { title: '', url: '', thumbnail: '' }]
    }));
  };

  const handleRemoveDeepLink = (index) => {
    const newLinks = [...draft.deepLinks];
    newLinks.splice(index, 1);
    setDraft(prev => ({ ...prev, deepLinks: newLinks }));
  };

  const handleDeepLinkChange = (index, field, value) => {
    const newLinks = [...draft.deepLinks];
    newLinks[index][field] = value;
    setDraft(prev => ({ ...prev, deepLinks: newLinks }));
  };

  const clearDraft = () => {
    setDraft(initialDraft);
    setUrlInput('');
    setManualTextInput('');
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col w-full overflow-hidden">
      {/* Admin Header */}
      <header className="bg-brand-surface border-b border-brand-border px-4 md:px-6 py-3 flex flex-wrap items-center justify-between gap-4 sticky top-0 z-50">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="w-6 h-6" />
            <span className="font-bold text-brand-text text-[15px]">Admin</span>
          </Link>
          <div className="hidden md:flex gap-1 bg-brand-bg p-1 rounded-[8px] border border-brand-border">
            {['News', 'Tools', 'Viral Threads', 'Settings'].map(tab => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  if (tab !== 'Settings') clearDraft();
                }}
                className={`px-4 py-1.5 rounded-[6px] text-[13px] font-bold transition-colors ${
                  activeTab === tab ? 'bg-brand-surface text-brand-text shadow-sm' : 'text-brand-secondary hover:text-brand-text'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
        
        {activeTab !== 'Settings' && (
          <div className="flex gap-2 bg-brand-bg p-1 rounded-[8px] border border-brand-border">
            <button
              onClick={() => setAdminMode('write')}
              className={`px-4 py-1.5 rounded-[6px] text-[13px] font-bold transition-colors ${
                adminMode === 'write' ? 'bg-brand-mint/10 text-brand-mint' : 'text-brand-secondary hover:text-brand-text'
              }`}
            >
              Write / Edit
            </button>
            <button
              onClick={() => setAdminMode('manage')}
              className={`px-4 py-1.5 rounded-[6px] text-[13px] font-bold transition-colors ${
                adminMode === 'manage' ? 'bg-brand-mint/10 text-brand-mint' : 'text-brand-secondary hover:text-brand-text'
              }`}
            >
              Manage
            </button>
          </div>
        )}
      </header>

      {/* Main Workspace */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        
        {activeTab === 'Settings' ? (
          <div className="w-full flex-1 bg-brand-bg overflow-y-auto p-4 md:p-8">
            <div className="max-w-2xl mx-auto space-y-8">
              <h1 className="text-[24px] font-bold text-brand-text mb-6">Global Site Settings</h1>
              
              <div className="bg-brand-surface border border-brand-border rounded-[8px] p-6">
                <h2 className="text-[16px] font-bold text-brand-text mb-2">Public Theme Control</h2>
                <p className="text-[13px] text-brand-secondary mb-6">
                  Select the theme that will be applied globally to all public visitors of the website.
                </p>
                
                <div className="flex gap-4">
                  <button
                    onClick={() => updateGlobalTheme('light')}
                    className={`flex-1 py-4 border-2 rounded-[8px] font-bold flex flex-col items-center gap-2 transition-all ${
                      theme === 'light' ? 'border-brand-text bg-brand-text text-brand-bg' : 'border-brand-border bg-brand-bg text-brand-text hover:border-brand-text/50'
                    }`}
                  >
                    <SafeIcon icon="Sun" className="w-6 h-6" />
                    Light Mode
                  </button>
                  <button
                    onClick={() => updateGlobalTheme('dark')}
                    className={`flex-1 py-4 border-2 rounded-[8px] font-bold flex flex-col items-center gap-2 transition-all ${
                      theme === 'dark' ? 'border-brand-text bg-brand-text text-brand-bg' : 'border-brand-border bg-brand-bg text-brand-text hover:border-brand-text/50'
                    }`}
                  >
                    <SafeIcon icon="Moon" className="w-6 h-6" />
                    Dark Mode
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : adminMode === 'manage' ? (
          <div className="w-full flex-1 bg-brand-bg overflow-y-auto p-4 md:p-8">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-[24px] font-bold text-brand-text mb-6">Manage {activeTab}</h1>

              {isLoadingItems ? (
                <div className="flex justify-center py-20">
                  <SafeIcon icon="Loader" className="w-8 h-8 text-brand-mint animate-spin" />
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-20 border border-dashed border-brand-border rounded-[12px]">
                  <p className="text-brand-secondary text-[14px]">No items found.</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {items.map(item => (
                    <div key={item.id} className="bg-brand-surface border border-brand-border rounded-[8px] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-brand-borderHover transition-colors">
                      <div className="flex-1 min-w-0 pr-4">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <span className={`text-[10px] font-mono uppercase font-bold px-2 py-0.5 rounded-[4px] ${item.status === 'published' ? 'bg-brand-mint/10 text-brand-mint' : 'bg-brand-trending/10 text-brand-trending'}`}>
                            {item.status}
                          </span>
                          {item.is_featured && (
                            <span className="text-[10px] font-mono uppercase font-bold px-2 py-0.5 rounded-[4px] bg-brand-text/10 text-brand-text">
                              Featured
                            </span>
                          )}
                          <span className="text-[12px] text-brand-muted">
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <h3 className="text-[15px] font-bold text-brand-text truncate">
                          {item.title || item.name || item.hook || 'Untitled'}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewItem(item)}
                          className="px-3 py-1.5 bg-brand-bg border border-brand-border rounded-[6px] text-[12px] font-bold text-brand-text hover:bg-brand-raised transition-colors flex items-center gap-1.5"
                        >
                          <SafeIcon icon="Eye" className="w-3.5 h-3.5" /> View
                        </button>
                        <button
                          onClick={() => handleEditItem(item)}
                          className="px-3 py-1.5 bg-brand-bg border border-brand-border rounded-[6px] text-[12px] font-bold text-brand-text hover:bg-brand-raised transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteItem(item.id)}
                          className="px-3 py-1.5 bg-brand-breaking/10 text-brand-breaking rounded-[6px] text-[12px] font-bold hover:bg-brand-breaking/20 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* WRITE / EDIT MODE */
          <>
            {/* Left Side - Editor */}
            <div className={`${viewMode === 'editor' ? 'flex' : 'hidden'} md:flex w-full md:w-1/2 border-r border-brand-border bg-brand-bg overflow-y-auto p-4 md:p-6 flex-col`}>
              <div className="max-w-xl mx-auto w-full space-y-6">
                <div className="flex justify-between items-end">
                  <div>
                    <h1 className="text-[20px] font-bold text-brand-text mb-1">
                      {draft.id ? `Editing ${activeTab}` : `Create New ${activeTab}`}
                    </h1>
                    <p className="text-brand-secondary text-[13px]">Generates via APIMart (with Web Search fallback).</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {draft.id && (
                      <button onClick={clearDraft} className="text-[11px] font-bold text-brand-muted hover:text-brand-text uppercase">
                        + New Post
                      </button>
                    )}
                    <button 
                      onClick={() => setUseManualText(!useManualText)}
                      className="text-[12px] text-brand-mint hover:opacity-80 font-medium transition-opacity"
                    >
                      {useManualText ? "Use URL Extraction" : "Paste Text Manually"}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <input
                    type="text"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="Source URL (e.g. https://...)"
                    className="w-full bg-brand-surface border border-brand-border rounded-[8px] px-3 py-2.5 text-[14px] text-brand-text focus:outline-none focus:border-brand-mint"
                  />
                  {useManualText && (
                    <textarea
                      value={manualTextInput}
                      onChange={(e) => setManualTextInput(e.target.value)}
                      placeholder="Paste article text here... (Limit 20000 chars)"
                      rows={4}
                      maxLength={20000}
                      className="w-full bg-brand-surface border border-brand-border rounded-[8px] px-3 py-2.5 text-[14px] text-brand-text resize-none focus:outline-none focus:border-brand-mint"
                    />
                  )}
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating || (!useManualText && !urlInput) || (useManualText && !manualTextInput)}
                    className="w-full py-3 bg-brand-text text-brand-bg rounded-[8px] text-[13px] font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isGenerating && !progressMsg.includes('Saving') ? (
                      <><SafeIcon icon="Loader" className="w-4 h-4 animate-spin" /> {progressMsg}</>
                    ) : 'Generate Draft'}
                  </button>
                </div>

                {error && (
                  <div className="p-3 bg-brand-breaking/10 border border-brand-breaking/20 rounded-[6px] text-brand-breaking text-[13px] font-medium">
                    {error}
                  </div>
                )}
                
                {success && (
                  <div className="p-3 bg-brand-mint/10 border border-brand-mint/20 rounded-[6px] text-brand-mint text-[13px] font-medium">
                    {success}
                  </div>
                )}

                <div className="h-px bg-brand-border w-full"></div>

                {/* Dynamic Editor Fields based on Tab */}
                <div className="space-y-4">
                  
                  {/* Featured & Category */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-brand-surface p-4 rounded-[8px] border border-brand-border">
                    <div className="flex-1">
                      <label className="block text-[10px] font-mono font-bold text-brand-text uppercase mb-1.5">Category</label>
                      <input
                        value={draft.category}
                        onChange={(e) => handleFieldChange('category', e.target.value)}
                        placeholder="e.g. OpenAI, Video, Agents"
                        className="w-full bg-brand-bg border border-brand-border rounded-[6px] px-3 py-2 text-brand-text text-[14px] focus:outline-none focus:border-brand-borderHover"
                      />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer pt-4 sm:pt-0">
                      <input 
                        type="checkbox" 
                        checked={draft.isFeatured}
                        onChange={(e) => handleFieldChange('isFeatured', e.target.checked)}
                        className="w-4 h-4 rounded border-brand-border bg-brand-bg text-brand-mint focus:ring-brand-mint focus:ring-offset-brand-bg cursor-pointer"
                      />
                      <span className="text-[13px] font-bold text-brand-text">Feature on Home</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-[10px] font-mono font-bold text-brand-text uppercase mb-1.5">
                      {activeTab === 'News' ? 'Hook' : 'Title / Name'} (Max 70 chars)
                    </label>
                    <textarea
                      value={draft.hook}
                      onChange={(e) => handleFieldChange('hook', e.target.value)}
                      rows={2}
                      maxLength={150}
                      placeholder="Catchy headline..."
                      className="w-full bg-brand-surface border border-brand-border rounded-[6px] px-3 py-2 text-brand-text text-[14px] resize-none focus:outline-none focus:border-brand-borderHover"
                    />
                  </div>

                  {activeTab === 'Tools' && (
                    <div>
                      <label className="block text-[10px] font-mono font-bold text-brand-text uppercase mb-1.5">Logo URL (Optional)</label>
                      <input
                        value={draft.logo || ''}
                        onChange={(e) => handleFieldChange('logo', e.target.value)}
                        placeholder="https://..."
                        className="w-full bg-brand-surface border border-brand-border rounded-[6px] px-3 py-2 text-brand-text text-[14px] focus:outline-none focus:border-brand-borderHover"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-[10px] font-mono font-bold text-brand-text uppercase mb-1.5">
                      {activeTab === 'Tools' ? 'Description' : 'Summary / Content'}
                    </label>
                    <textarea
                      value={draft.summary}
                      onChange={(e) => handleFieldChange('summary', e.target.value)}
                      rows={6}
                      maxLength={20000}
                      placeholder="Detailed explanation..."
                      className="w-full bg-brand-surface border border-brand-border rounded-[6px] px-3 py-2 text-brand-text text-[14px] resize-none focus:outline-none focus:border-brand-borderHover"
                    />
                  </div>

                  {activeTab === 'News' && (
                    <>
                      <div>
                        <label className="block text-[10px] font-mono font-bold text-brand-text uppercase mb-1.5">Key Points</label>
                        <div className="space-y-2">
                          {draft.points.map((point, idx) => (
                            <input
                              key={idx}
                              value={point}
                              maxLength={300}
                              onChange={(e) => handlePointChange(idx, e.target.value)}
                              placeholder={`Point ${idx + 1}`}
                              className="w-full bg-brand-surface border border-brand-border rounded-[6px] px-3 py-2 text-brand-text text-[14px] focus:outline-none focus:border-brand-borderHover"
                            />
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-mono font-bold text-brand-text uppercase mb-1.5">Why It Matters</label>
                        <textarea
                          value={draft.whyItMatters}
                          onChange={(e) => handleFieldChange('whyItMatters', e.target.value)}
                          rows={2}
                          maxLength={1000}
                          placeholder="The broader impact..."
                          className="w-full bg-brand-mint/5 border border-brand-mint/20 rounded-[6px] px-3 py-2 text-brand-text text-[14px] resize-none focus:outline-none focus:border-brand-mint/40"
                        />
                      </div>
                    </>
                  )}

                  {/* Universal CTA Section */}
                  <div className="pt-4 border-t border-brand-border">
                    <label className="block text-[10px] font-mono font-bold text-brand-text uppercase mb-3">Call To Action (Optional)</label>
                    <div className="space-y-3">
                      <input
                        placeholder="CTA Text (e.g. Read Full Article, Try this tool)"
                        value={draft.ctaText || ''}
                        maxLength={100}
                        onChange={(e) => handleFieldChange('ctaText', e.target.value)}
                        className="w-full bg-brand-bg border border-brand-border rounded-[6px] px-3 py-2.5 text-[13px] text-brand-text focus:outline-none focus:border-brand-mint"
                      />
                      <input
                        placeholder="CTA Link URL (https://...)"
                        value={draft.ctaUrl || ''}
                        onChange={(e) => handleFieldChange('ctaUrl', e.target.value)}
                        className="w-full bg-brand-bg border border-brand-border rounded-[6px] px-3 py-2.5 text-[13px] text-brand-text focus:outline-none focus:border-brand-mint"
                      />
                    </div>
                  </div>

                  {/* Universal Deep Links Section */}
                  <div className="pt-4 border-t border-brand-border">
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-[10px] font-mono font-bold text-brand-text uppercase">Deep Links</label>
                      <button
                        onClick={handleAddDeepLink}
                        className="text-[10px] font-bold text-brand-mint uppercase hover:text-brand-text transition-colors"
                      >
                        + Add Link
                      </button>
                    </div>
                    
                    {(!draft.deepLinks || draft.deepLinks.length === 0) && (
                      <p className="text-[12px] text-brand-secondary italic">No deep links added.</p>
                    )}

                    <div className="space-y-3">
                      {draft.deepLinks?.map((link, idx) => (
                        <div key={idx} className="p-3 bg-brand-surface border border-brand-border rounded-[8px] space-y-2 relative">
                          <button 
                            onClick={() => handleRemoveDeepLink(idx)}
                            className="absolute top-3 right-3 text-brand-muted hover:text-brand-breaking transition-colors"
                          >
                            <SafeIcon icon="X" className="w-4 h-4" />
                          </button>
                          <input
                            placeholder="Link Title (e.g. Read Full Paper)"
                            value={link.title}
                            maxLength={100}
                            onChange={(e) => handleDeepLinkChange(idx, 'title', e.target.value)}
                            className="w-full bg-brand-bg border border-brand-border rounded-[6px] px-3 py-1.5 text-[13px] text-brand-text focus:outline-none focus:border-brand-borderHover"
                          />
                          <input
                            placeholder="URL (https://...)"
                            value={link.url}
                            onChange={(e) => handleDeepLinkChange(idx, 'url', e.target.value)}
                            className="w-full bg-brand-bg border border-brand-border rounded-[6px] px-3 py-1.5 text-[13px] text-brand-text focus:outline-none focus:border-brand-borderHover"
                          />
                          <input
                            placeholder="Thumbnail Image URL (Optional)"
                            value={link.thumbnail}
                            onChange={(e) => handleDeepLinkChange(idx, 'thumbnail', e.target.value)}
                            className="w-full bg-brand-bg border border-brand-border rounded-[6px] px-3 py-1.5 text-[13px] text-brand-text focus:outline-none focus:border-brand-borderHover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
              <div className="h-10 md:hidden"></div>
            </div>

            {/* Right Side - Live Preview */}
            <div className={`${viewMode === 'preview' ? 'flex' : 'hidden'} md:flex w-full md:w-1/2 bg-brand-surface relative overflow-y-auto p-4 md:p-8 flex-col items-center justify-start md:justify-center`}>
              <div className="w-full max-w-[600px] mb-6 flex justify-between items-center">
                <div className="flex flex-col">
                  <h2 className="text-brand-text text-[11px] font-mono font-bold uppercase tracking-widest">Live Preview</h2>
                  {draft.usedFallback && (
                    <span className="text-brand-trending text-[10px] font-mono mt-1">Generated using web research fallback</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleSave('draft')}
                    disabled={isGenerating || !draft.hook}
                    className={`px-4 py-2 rounded-[6px] border text-[13px] font-bold transition-colors disabled:opacity-50 ${
                      draft.status === 'draft' ? 'bg-brand-raised border-brand-borderHover text-brand-text' : 'border-brand-border text-brand-secondary hover:text-brand-text'
                    }`}
                  >
                    {draft.id && draft.status === 'draft' ? 'Update Draft' : 'Save Draft'}
                  </button>
                  <button 
                    onClick={() => handleSave('published')}
                    disabled={isGenerating || !draft.hook}
                    className="px-4 py-2 rounded-[6px] bg-brand-mint text-brand-bg text-[13px] font-bold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
                  >
                    {isGenerating && progressMsg.includes('Saving') && <SafeIcon icon="Loader" className="w-3 h-3 animate-spin" />}
                    {draft.id && draft.status === 'published' ? 'Update Live' : 'Publish'}
                  </button>
                </div>
              </div>
              
              <div className="w-full max-w-[600px] pointer-events-none">
                {draft.hook ? (
                  activeTab === 'News' ? (
                    <NewsCard news={draft} />
                  ) : activeTab === 'Tools' ? (
                    <ToolCard tool={{...draft, name: draft.hook, description: draft.summary, bestFor: [], features: [] }} />
                  ) : (
                    <ThreadCard thread={{...draft, title: draft.hook, content: draft.summary}} />
                  )
                ) : (
                  <div className="editorial-card p-10 flex flex-col items-center justify-center text-center border-dashed">
                    <SafeIcon icon="FileText" className="w-8 h-8 text-brand-muted mb-3" />
                    <p className="text-brand-secondary text-[14px]">Your preview will appear here</p>
                  </div>
                )}
              </div>
              <div className="h-10 md:hidden"></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;